import { Home, Wind, Sun, Car, Archive, Accessibility, Bike } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const Highlights = () => {
    const t = useT();

    const HIGHLIGHTS = [
        {
            icon: Home,
            title: t({ de: "4 Eigentums\u00ADwohnungen", en: "4 condominiums" }),
            text: t({
                de: "3-Familienhaus mit Loft-Maisonette im Hintergebäude. Wenig Gemeinschaftseigentum, kein Aufzug, wartungsarm.",
                en: "Three-family front building plus a loft maisonette in the rear. Minimal shared property, no elevator, low maintenance.",
            }),
        },
        {
            icon: Wind,
            title: t({ de: "Eigene Wärmepumpe", en: "Dedicated heat pump" }),
            text: t({
                de: "Je Wohnung eine eigene Luft-Luft-Wärmepumpe – moderne Bauweise im Standard Effizienzhaus 40.",
                en: "Each apartment has its own air-to-air heat pump – modern construction at energy class 40.",
            }),
        },
        {
            icon: Sun,
            title: t({ de: "PV-Anlage inklusive", en: "PV system included" }),
            text: t({
                de: "2 kW Photovoltaik-Anlage inkl. passendem Stromspeicher separat für jede Wohnung. Wallbox an jedem Stellplatz vorbereitet (auf Wunsch optional installiert).",
                en: "2 kW PV system with a dedicated matching battery storage per apartment. Wallbox prepared at every parking spot (optionally installed on request).",
            }),
        },
        {
            icon: Car,
            title: t({ de: "Carport pro Wohnung", en: "Carport per apartment" }),
            text: t({
                de: "Je Wohnung ein PKW-Carport im Hintergebäude (EG). Wallbox-Anschluss vorbereitet – Wallbox optional auf Wunsch.",
                en: "One covered carport per apartment in the rear building – wallbox connection prepared, wallbox optional on request.",
            }),
        },
        {
            icon: Bike,
            title: t({ de: "Fahrradständer im Innenhof", en: "Bike racks in the courtyard" }),
            text: t({
                de: "Im kleinen Innenhof befinden sich Fahrradständer für alle vier Einheiten – kurze Wege, sicher abgestellt.",
                en: "Bike racks for all four units in the small inner courtyard – short paths, safely parked.",
            }),
        },
        {
            icon: Archive,
            title: t({ de: "Eigener Kellerraum", en: "Private storage room" }),
            text: t({
                de: "Jede Wohnung mit privatem Kellerraum – ideal für Stauraum, Hobby oder Hauswirtschaft.",
                en: "Every apartment has a private basement room – ideal for storage, hobby or utility.",
            }),
        },
        {
            icon: Accessibility,
            title: t({ de: "EG barrierefrei", en: "Ground floor: barrier-free" }),
            text: t({
                de: "Erdgeschoss\u00ADwohnung mit barrierefreiem Zugang – komfortabler Einstieg in jeder Lebensphase.",
                en: "Ground-floor apartment with barrier-free access – comfortable for every stage of life.",
            }),
        },
    ];

    return (
        <section className="bg-cream py-24 sm:py-32" data-testid="highlights-section">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                    <div className="lg:col-span-5">
                        <span className="overline" data-testid="highlights-eyebrow">
                            {t({ de: "01 / 12 — Das Projekt G21", en: "01 / 12 — The project G21" })}
                        </span>
                        <h2 className="h-section mt-4 text-balance" data-testid="highlights-title">
                            {t({ de: "Wohnen mit Tiefgang –", en: "Living with depth –" })}
                            <span className="italic text-brand-500">
                                {t({ de: " nicht von der Stange.", en: " not off the rack." })}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="body-lg" data-testid="highlights-intro">
                            {t({
                                de: "Mit dem Projekt G21 entsteht in Wiesbaden-Biebrich ein modernes Wohnensemble mit vier hochwertigen Eigentumswohnungen. Die Architektur kombiniert klassische Dachformen mit modernen Elementen – Satteldach mit integriertem Flachbereich für klassische Silhouette und großzügige Dachterrassen.",
                                en: "Project G21 in Wiesbaden-Biebrich is a modern residential ensemble with four high-quality condominiums. The architecture pairs classical roof shapes with modern elements – a gable roof with an integrated flat section for a clean silhouette and generous roof terraces.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {HIGHLIGHTS.map((h, i) => {
                        const Icon = h.icon;
                        const lastWide = i === HIGHLIGHTS.length - 1;
                        return (
                            <div
                                key={i}
                                className={`bg-cream p-8 lg:p-10 group transition-colors hover:bg-white ${
                                    lastWide ? "lg:col-span-3" : ""
                                }`}
                                data-testid={`highlight-card-${i}`}
                            >
                                <div className={`flex items-start gap-5 ${lastWide ? "lg:max-w-3xl" : ""}`}>
                                    <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-brand-50 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-colors">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h3 className="font-heading text-xl text-ink mb-2">
                                            {h.title}
                                        </h3>
                                        <p className="font-body text-sm text-stone-600 leading-relaxed">
                                            {h.text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
